/**
 * MP Sticky Buy (PDP · mobile)
 * ADDED: 2026-08-07 by MeshPlanet
 * 
 * The theme ships its own sticky bar (assets/sticky-add-to-cart.js), a
 * condensed *copy* of the add to cart button that on mobile is pinned open for
 * the whole page. This keeps the real block on screen instead: the group that
 * holds the guarantees row and the add to cart button, plus the ship/delivery
 * widget that sits right under it.
 *
 *   .group-block-content              (product details column)
 *     ├─ variant-picker
 *     ├─ .mp-sticky-buy               <- wrapper added here, holds the height
 *     │    └─ .mp-sticky-buy__inner   <- goes position:fixed once stuck
 *     │         ├─ .mp-sticky-buy__variants  (only shown while pinned)
 *     │         ├─ .group-block       (buy buttons + mp-pdp-guarantees)
 *     │         └─ mp-shipping-dates
 *     └─ …
 *
 * Wrapping the real nodes instead of cloning them keeps a single product form
 * on the page, so variant changes, quantity rules, the Stylux offer and the
 * shipping dates keep working with no extra wiring. mp-pdp-guarantees.js moves
 * its row inside the button's own container, which is inside the group either
 * way, so the guarantees come along on their own.
 *
 * The option row is the one part that *is* a copy: the real variant picker sits
 * above the block and is far too tall to pin, so a compact swatch row is
 * rendered by the snippet and mirrors the picker in both directions — a tap
 * clicks the matching radio, and 'variant:update' reads the picker back.
 *
 * A tap also writes the target variant into the product form up front. The
 * theme only does that once the section fetch resolves (assets/product-form.js
 * #onVariantUpdate), and here the swatches sit a thumb's width above the
 * button — tapping a colour and then add to cart inside that window would post
 * the *previous* variant. The theme's own update still lands afterwards and
 * overwrites the id with the authoritative one.
 *
 * While pinned the bar publishes its own height as --mp-sticky-buy-height on
 * the root element, so fixed widgets anchored to the bottom of the viewport can
 * step over it — see the Gorgias chat bubble rule in the snippet's stylesheet.
 *
 * By default the bar is only ever revealed by scrolling *down* past the block,
 * so landing below it from a restored scroll position or an in page anchor
 * leaves it hidden. Once shown it stays until the block scrolls back into view.
 * With data-reveal="on_load" it is pinned from the start instead, whenever the
 * real block is off screen in either direction — the block is still below the
 * fold on load, and the bar hands back to it once it scrolls into view. Over
 * the footer it is only suspended so it never covers it, and comes straight
 * back once the footer leaves.
 *
 * Data attributes:
 *   data-product-id  Product the option row must drive, matched on the picker
 *   data-reveal      'after_block' (default) or 'on_load', the section setting
 *
 * SEE ALSO: snippets/mp-sticky-buy.liquid, sections/product-information.liquid
 */
(function () {
  'use strict';

  var MOBILE_QUERY = '(max-width: 749px)';

  /* iOS rubber banding and sub-pixel jitter both report a scroll of a fraction
     of a pixel, which is not a gesture and must not reveal the bar. */
  var DIRECTION_THRESHOLD = 2;

  class MpStickyBuy extends HTMLElement {
    constructor() {
      super();

      /** @type {HTMLElement | null} */
      this.wrapper = null;
      /** @type {HTMLElement | null} */
      this.inner = null;
      /** @type {HTMLElement | null} */
      this.variants = null;
      /** @type {IntersectionObserver | null} */
      this.blockObserver = null;
      /** @type {IntersectionObserver | null} */
      this.footerObserver = null;
      /** @type {MutationObserver | null} */
      this.domObserver = null;
      /** @type {ResizeObserver | null} */
      this.heightObserver = null;
      /** @type {AbortController | null} */
      this.controller = null;
      /** @type {'above' | 'visible' | 'below'} */
      this.blockPosition = 'visible';
      /** @type {'down' | 'up' | null} */
      this.scrollDirection = null;
      this.lastScrollY = 0;
      this.footerVisible = false;
      this.revealed = false;
      this.stuck = false;
    }

    connectedCallback() {
      var self = this;

      this.variants = /** @type {HTMLElement | null} */ (this.querySelector('.mp-sticky-buy__variants'));
      this.media = window.matchMedia(MOBILE_QUERY);
      this.controller = new AbortController();
      this.lastScrollY = window.scrollY;

      var signal = this.controller.signal;

      this.media.addEventListener('change', function () {
        self.sync();
      }, { signal: signal });

      window.addEventListener('scroll', function () {
        self.trackScroll();
      }, { passive: true, signal: signal });

      /* Both events bubble to the document, and either one can land first
         depending on whether the picker had to fetch a new section. */
      document.addEventListener('variant:update', function () {
        self.syncVariants();
      }, { signal: signal });

      document.addEventListener('variant:selected', function () {
        self.syncVariants();
      }, { signal: signal });

      document.addEventListener('click', function (event) {
        self.handleClick(event);
      }, { signal: signal });

      this.sync();
    }

    disconnectedCallback() {
      if (this.controller) this.controller.abort();
      this.unwatch();
      this.teardown();
    }

    /** @returns {HTMLElement | null} The product details column of this section. */
    get scope() {
      var section = this.closest('.shopify-section');

      return /** @type {HTMLElement | null} */ ((section || document).querySelector('.product-details'));
    }

    /** @returns {HTMLElement | null} The real variant picker the row mirrors. */
    get picker() {
      var section = this.closest('.shopify-section') || document;
      var productId = this.dataset.productId;
      var selector = productId ? 'variant-picker[data-product-id="' + productId + '"]' : 'variant-picker';

      return /** @type {HTMLElement | null} */ (section.querySelector(selector));
    }

    /**
     * @returns {HTMLElement | null} The main buy form. Scoped to the buy
     * buttons block on purpose: the upsell blocks in the same column render
     * product forms of their own (blocks/mp-pdp-upsell.liquid).
     */
    get form() {
      var root = this.inner || this.scope;

      return root ? /** @type {HTMLElement | null} */ (root.querySelector('.buy-buttons-block product-form-component')) : null;
    }

    /** Wraps on mobile, unwraps everywhere else. */
    sync() {
      if (!this.isConnected) return;

      if (this.media.matches) this.setup();
      else this.teardown();
    }

    setup() {
      if (this.wrapper) return;

      var scope = this.scope;
      var found = scope && this.collect(scope);

      /* The element renders after the product details, so a miss means the
         theme editor is mid re-render, or the merchant removed the block. */
      if (!found) {
        this.watch();
        return;
      }

      this.unwatch();

      var wrapper = document.createElement('div');
      wrapper.className = 'mp-sticky-buy';
      wrapper.dataset.stuck = 'false';

      var inner = document.createElement('div');
      inner.className = 'mp-sticky-buy__inner';
      wrapper.appendChild(inner);

      /* The nodes were flex children of the column, so the space between them
         came from the column's own gap. The wrapper takes it over, which keeps
         the unstuck page pixel identical to before. */
      var gap = window.getComputedStyle(found.parent).rowGap;
      if (gap && gap !== 'normal') wrapper.style.setProperty('--mp-sticky-buy-flow-gap', gap);

      found.parent.insertBefore(wrapper, found.nodes[0]);
      found.nodes.forEach(function (node) {
        inner.appendChild(node);
      });

      /* Above the guarantees, and hidden by CSS until the bar is pinned so it
         never doubles up with the real picker further up the column. */
      if (this.variants) inner.insertBefore(this.variants, inner.firstChild);

      this.wrapper = wrapper;
      this.inner = inner;

      /* The pinned height changes with the option row, a wrapped guarantees
         line or a variant swap, and anything stepping over the bar has to
         follow it. */
      var self = this;
      this.heightObserver = new ResizeObserver(function () {
        self.publishHeight();
      });
      this.heightObserver.observe(inner);

      this.syncVariants();
      this.observe();
    }

    teardown() {
      if (this.blockObserver) {
        this.blockObserver.disconnect();
        this.blockObserver = null;
      }

      if (this.footerObserver) {
        this.footerObserver.disconnect();
        this.footerObserver = null;
      }

      if (this.heightObserver) {
        this.heightObserver.disconnect();
        this.heightObserver = null;
      }

      if (!this.wrapper) return;

      this.unstick();

      /* Back into the marker element first: everything left in the inner
         wrapper returns to the column, and the row must not go with it. */
      if (this.variants) this.appendChild(this.variants);

      var parent = this.wrapper.parentElement;

      if (parent && this.inner) {
        while (this.inner.firstChild) parent.insertBefore(this.inner.firstChild, this.wrapper);
      }

      this.wrapper.remove();
      this.wrapper = null;
      this.inner = null;
      this.blockPosition = 'visible';
      this.footerVisible = false;
      this.revealed = false;
    }

    /**
     * The sticky unit is the group that holds the buy buttons, followed by the
     * shipping widget when it sits right after it in the same column.
     *
     * @param {HTMLElement} scope
     * @returns {{ parent: HTMLElement, nodes: Element[] } | null}
     */
    collect(scope) {
      var buyButtons = scope.querySelector('.buy-buttons-block');

      if (!buyButtons) return null;

      /* The column itself is a group block too — wrapping that one would pin
         the whole product details, so it is never a valid start. */
      var rootGroup = scope.querySelector(':scope > .group-block');
      var group = buyButtons.closest('.group-block');
      var start = group && group !== rootGroup ? group : buyButtons;
      var parent = start.parentElement;

      if (!parent) return null;

      var nodes = [start];
      var shipping = scope.querySelector('mp-shipping-dates');

      /* A merchant can move the widget elsewhere; anything that isn't a
         following sibling of the group is left where it is. */
      if (shipping && shipping.parentElement === parent) {
        var node = start.nextElementSibling;
        var between = [];

        while (node) {
          between.push(node);

          if (node === shipping) {
            nodes = nodes.concat(between);
            break;
          }

          node = node.nextElementSibling;
        }
      }

      return { parent: parent, nodes: nodes };
    }

    observe() {
      var self = this;

      this.blockObserver = new IntersectionObserver(function (entries) {
        var entry = entries[entries.length - 1];

        if (!entry) return;

        if (entry.isIntersecting) self.blockPosition = 'visible';
        else self.blockPosition = entry.boundingClientRect.bottom <= 0 ? 'above' : 'below';

        self.update();
      });

      this.blockObserver.observe(/** @type {HTMLElement} */ (this.wrapper));

      // In themes migrated from 2.0, the footer element doesn't exist.
      var footer = document.querySelector('footer') || document.querySelector('[class*="footer-group"]');

      if (!footer) return;

      this.footerObserver = new IntersectionObserver(function (entries) {
        var entry = entries[entries.length - 1];

        if (!entry) return;

        self.footerVisible = entry.isIntersecting;
        self.update();
      });

      this.footerObserver.observe(footer);
    }

    trackScroll() {
      var y = window.scrollY;
      var delta = y - this.lastScrollY;

      if (Math.abs(delta) < DIRECTION_THRESHOLD) return;

      this.lastScrollY = y;
      this.scrollDirection = delta > 0 ? 'down' : 'up';
      this.update();
    }

    update() {
      /* Anywhere but on top of the real block, which is already in view. */
      if (this.dataset.reveal === 'on_load') {
        this.revealed = this.blockPosition !== 'visible';
      }

      /* Revealing takes both: the block is off the top *and* the last move was
         downwards. Landing below the block without scrolling — a restored
         position, an anchor jump — leaves the bar hidden. Scrolling back up
         doesn't take it away again; only reaching the real block does. */
      else if (this.blockPosition !== 'above') this.revealed = false;
      else if (this.scrollDirection === 'down') this.revealed = true;

      /* Kept as a flag of its own rather than read off the current scroll
         direction, because the footer only *suspends* the bar: leaving the
         footer is an upward move, and deriving the reveal there would hold the
         bar back until the shopper scrolled down one more time. */
      if (this.revealed && !this.footerVisible) this.stick();
      else this.unstick();
    }

    stick() {
      if (this.stuck || !this.wrapper || !this.inner) return;

      /* Locked before the switch to fixed: without it the wrapper collapses to
         nothing, the page jumps up, the block scrolls back into view and the
         observer unsticks it again on the next frame. Measured while the option
         row is still hidden, which is what the column actually gives up. */
      this.wrapper.style.minHeight = this.inner.offsetHeight + 'px';
      this.wrapper.dataset.stuck = 'true';
      this.stuck = true;

      /* After the flip, so the measurement is the pinned box: padding, option
         row and all. */
      this.publishHeight();
    }

    unstick() {
      if (!this.stuck || !this.wrapper) return;

      this.wrapper.dataset.stuck = 'false';
      this.wrapper.style.minHeight = '';
      this.stuck = false;
      this.publishHeight();
    }

    /**
     * Publishes the pinned bar's height as --mp-sticky-buy-height on the root,
     * and removes it when nothing is pinned. Fixed widgets anchored to the
     * bottom of the viewport read it to stay clear of the bar; the Gorgias chat
     * bubble sits at bottom: 40px and would land on the add to cart button.
     */
    publishHeight() {
      var root = document.documentElement;

      if (!this.stuck || !this.inner) {
        root.style.removeProperty('--mp-sticky-buy-height');
        return;
      }

      root.style.setProperty(
        '--mp-sticky-buy-height',
        Math.round(this.inner.getBoundingClientRect().height) + 'px'
      );
    }

    /**
     * @param {Event} event
     */
    handleClick(event) {
      var target = event.target;

      if (!(target instanceof Element)) return;

      var swatch = target.closest('.mp-sticky-buy__swatch');

      if (!swatch || !this.variants || !this.variants.contains(swatch)) return;

      var picker = this.picker;
      var input = picker && this.findInput(picker, swatch.dataset.optionPosition, swatch.dataset.optionValue);

      if (!input || input.checked) return;

      /* Written before the click so a fast follow up tap on add to cart can't
         post the variant that is being replaced. */
      this.claimVariant(input.dataset.variantId);

      /* The real radio drives everything downstream — price, media, the add to
         cart button, the shipping dates — and the row reads it back below. */
      input.click();
      this.syncVariants();
    }

    /**
     * Points the form at the variant the tap resolves to, ahead of the theme.
     *
     * @param {string | undefined} variantId - From the radio's data-variant-id.
     */
    claimVariant(variantId) {
      var form = this.form;

      if (!form) return;

      var input = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="id"]'));

      if (!input) return;

      if (variantId) {
        input.value = variantId;
        return;
      }

      /* The picker offers no variant for this value under the current
         selection, so there is nothing safe to post: hold the button until the
         theme reports what the selection actually resolved to, which is when it
         enables or disables the button itself. */
      var container = /** @type {HTMLElement & { disable?: () => void }} */ (
        form.querySelector('add-to-cart-component')
      );

      if (container && typeof container.disable === 'function') {
        container.disable();
        return;
      }

      var button = /** @type {HTMLButtonElement | null} */ (form.querySelector('[ref="addToCartButton"]'));
      if (button) button.disabled = true;
    }

    /**
     * Mirrors the picker's current selection and availability onto the row.
     * Reading the picker rather than tracking state of its own means the row
     * can't drift, however the variant changed.
     */
    syncVariants() {
      if (!this.variants) return;

      var picker = this.picker;

      if (!picker) return;

      var options = this.variants.querySelectorAll('.mp-sticky-buy__option');

      for (var i = 0; i < options.length; i++) {
        var option = /** @type {HTMLElement} */ (options[i]);
        var position = option.dataset.optionPosition;
        var swatches = option.querySelectorAll('.mp-sticky-buy__swatch');
        var selected = '';

        for (var j = 0; j < swatches.length; j++) {
          var swatch = /** @type {HTMLElement} */ (swatches[j]);
          var input = this.findInput(picker, position, swatch.dataset.optionValue);
          var checked = !!input && input.checked;

          if (checked) selected = swatch.dataset.optionValue || '';

          swatch.classList.toggle('mp-sticky-buy__swatch--active', checked);
          swatch.setAttribute('aria-pressed', checked ? 'true' : 'false');

          /* A value the picker no longer offers an input for is one the current
             selection ruled out, so it reads as unavailable too. */
          swatch.classList.toggle(
            'mp-sticky-buy__swatch--unavailable',
            !input || input.dataset.optionAvailable === 'false'
          );
        }

        var value = option.querySelector('[data-mp-option-value]');
        if (value && selected) value.textContent = selected;
      }
    }

    /**
     * @param {Element} picker
     * @param {string | undefined} position
     * @param {string | undefined} value
     * @returns {HTMLInputElement | null}
     */
    findInput(picker, position, value) {
      if (!position || !value) return null;

      return /** @type {HTMLInputElement | null} */ (
        picker.querySelector(
          'input[type="radio"][data-input-id^="' + position + '-"][value="' + CSS.escape(value) + '"]'
        )
      );
    }

    /** Retries the wrap while the blocks it needs aren't in the DOM yet. */
    watch() {
      if (this.domObserver) return;

      var root = this.closest('.shopify-section');

      if (!root) return;

      var self = this;

      this.domObserver = new MutationObserver(function () {
        self.setup();
      });

      this.domObserver.observe(root, { childList: true, subtree: true });
    }

    unwatch() {
      if (!this.domObserver) return;

      this.domObserver.disconnect();
      this.domObserver = null;
    }
  }

  if (!customElements.get('mp-sticky-buy')) {
    customElements.define('mp-sticky-buy', MpStickyBuy);
  }
})();
